import { Injectable } from '@angular/core';
// Database imports
import { HttpClient } from '@angular/common/http';

// Rx JS imports
import { Observable, of, combineLatest, empty, throwError, firstValueFrom } from 'rxjs';
import { map, first, switchMap, take, catchError } from 'rxjs/operators';

// Models
import { QueryFn } from './database';
import { DatabaseService } from './database.service';
import { Challenge, MotesPaths, StatEventVoucherRedeemed, StatEventVoucherClaimed, Payment, Product, ProductType, Mote, DatabaseModel, DistributionChannel, ModelJson } from '../models';
import { ProductVoucher, Voucher } from '../models/voucher';
import { StatsService } from './stats.service';
import { MotesLoginService } from '../motes-login';
import { ProductService } from './product.service';
import { MoteService } from './mote.service';
import { ProjectService } from './project.service';
import { CreateVoucherRequestBody, SendVoucherEmailBody, CreateVoucherRsp } from '../api-models/vouchers';


@Injectable({ providedIn: 'root' })
export class VoucherService {

    constructor(private af: DatabaseService, private statsService: StatsService, private productService: ProductService, private httpClient: HttpClient, private loginService: MotesLoginService, private moteService: MoteService, private projectService: ProjectService) {
        if(typeof window == 'undefined') return;
        // Expose VoucherService to Cypress e2e test 
        if((window as any)?.Cypress) {
            (window as any).VoucherService = this;
        }
    }

    public init() {
    }

    /**
     * Create a voucher for a specific product and assign it to a user
     */
    public async createUserVoucher(productKey: string, userKey: string, email: string, quizPoints: number): Promise<Voucher> {
        let voucher = new Voucher();
        voucher.userKey = userKey;
        voucher.productKey = productKey;
        voucher.createdOn = this.af.serverTime();
        voucher.quizPoints = quizPoints;
        voucher.email = email || '';

        // Save the voucher
        let vouchers = await this.createVoucher(voucher, 1);
        return (vouchers && vouchers.length > 0) ? vouchers[0] : null;
    }

    /**
     * @deprecated Use 'createVouchers()' instead (below)
     */
    public async createVoucher(voucher: Voucher, amount: number): Promise<Voucher[]> {
        const headers = await this.loginService.getAuthOptions();
        const userRights = await this.loginService.getUserCurrentRights();
        const distributionChannel = userRights.isSuperAdmin ? DistributionChannel.StoryHuntAdmin : DistributionChannel.StoryHuntCreator;

        // Create voucher request
        let req = this.httpClient.post<{ vouchers: Voucher[], status: string }>('/api/vouchers', {
            "amount": amount,
            "productKey": voucher.productKey,
            "email": voucher.email,
            "comments": voucher.comments,
            "userKey": voucher.userKey,
            "quizPoints": voucher.quizPoints,
            "distributionChannel": distributionChannel
        }, headers);

        // Parse response and create voucher model
        return req.pipe(map(rsp => {
            if(!rsp.vouchers) {
                console.warn("Create voucher failed ", rsp);
                return null;
            }
            return rsp.vouchers.map(json => new Voucher(json, json.key));
        })).toPromise();
    }

    /**
     * Server side implementation of create voucher - from Creator 'Tickets' (admin  only)
     */
    public async createVouchers(email: string, productKey: string, price: number, amount: number, distributionChannel: DistributionChannel, comments: string): Promise<number> {
        const headers = await this.loginService.getAuthOptions();
        const body: CreateVoucherRequestBody = {
            email,
            productKey,
            price,
            amount,
            comments,
            distributionChannel,
            isFreeTicket: price == 0,
            paymentId: 'motes_free_ticket',
            statEventDisabled: (price == 0) || (distributionChannel == DistributionChannel.CypressTest)
        }

        // Create voucher request
        const req = this.httpClient.post<CreateVoucherRsp>(MotesPaths.VOUCHER_REST_API(), body, headers);

        return req.toPromise().then((rsp) => {
            console.log("Vouchers created: ", rsp);
            return rsp.vouchersAmount;
        }).catch(err => {
            console.log("Could not create vouchers: ", err);
            return null;
        });
    }

    /**
     * Send voucher email for existing vouchers
     */
    public async sendVoucherEmail(body: SendVoucherEmailBody): Promise<boolean> {
        const headers = await this.loginService.getAuthOptions();
        let req = this.httpClient.post<{ status: string }>(MotesPaths.VOUCHER_REST_API('send-email'), body, headers);

        return req.toPromise().then((status) => true).catch(err => {
            console.log("Could not send voucher email: ", err);
            return false;
        });
    }

    public async deleteVoucher(voucherKey: string): Promise<any> {
        if(!voucherKey) return;
        // Delete voucher parent
        await this.af.database.ref(MotesPaths.VOUCHER_DB_PATH(voucherKey)).remove();
        // Delete voucher children
        let voucherChildren = await this.getVoucherChildren(voucherKey);
        let childPromises = voucherChildren.map((child) => this.af.database.ref(MotesPaths.VOUCHER_DB_PATH(child.key)).remove());
        return Promise.all(childPromises);
    }

    /**
    * Get a list of vouchers to a specific product associated with a user
    */
    public getUserProductVoucher(productKey: string, userKey: string): Promise<Voucher[]> {
        return this.watchUserProductVouchers(productKey, userKey).pipe(first()).toPromise();
    }

    /**
     * Watch a list of ProductVoucherLists associated with a user
     */
    public watchProductVoucherLists(userKey: string): Observable<ProductVoucherList[]> {
        return this.watchUserVouchers(userKey).pipe(switchMap((vouchers) => {
            let vouchersByProduct = {};
            // Add voucher to userVouchers map
            for(let voucher of vouchers) {
                if(!vouchersByProduct[voucher.productKey]) vouchersByProduct[voucher.productKey] = [];
                vouchersByProduct[voucher.productKey].push(voucher);
            }

            let productVoucherListsObs = Object.keys(vouchersByProduct).map((productKey) => {
                return this.watchProductVoucherList(productKey, vouchersByProduct[productKey]);
            });

            if(productVoucherListsObs.length <= 0) return of(null);
            return combineLatest(productVoucherListsObs);
        }));
    }

    /**
     * Watch vouchers in pool
     */
    public watchPool(poolKey: string): Observable<Voucher[]> {
        const query = (ref) => ref.orderByChild('poolKey').equalTo(poolKey);

        return this.af.database.list(MotesPaths.VOUCHER_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            // Get payload json from firebase and add key in data (to be compatible with current impl)
            if(!snapList) return [];
            return snapList.map((snap) => new Voucher(snap.payload.val(), snap.key));
        }));
    }

    public watchPoolInfo(poolKey: string, userKey: string = ''): Observable<PoolInfo> {
        if(!poolKey) return of(null);
        let poolInfo = new PoolInfo(null, poolKey);

        return this.watchPool(poolKey).pipe(switchMap((pool: Voucher[]) => {
            // Split pool in available and claimed
            poolInfo.availableVouchers = pool ? pool.filter((voucher) => !voucher.userKey) : [];
            poolInfo.claimedVouchers = pool ? pool.filter((voucher) => voucher.userKey) : [];
            poolInfo.claimCode = (pool?.length > 0) ? pool[0].claimCode : "";
            if(userKey) poolInfo.ownVoucher = poolInfo.claimedVouchers.find((voucher) => voucher.userKey == userKey);
            if(!pool || pool.length <= 0) return of(null);
            // Fetch pool product
            return this.productService.watchProduct(pool[0].productKey);
        }), switchMap((product: Product) => {
            poolInfo.product = product;
            if(!product) return of(null);
            // Fetch pool tour
            return this.moteService.watchMote(product.tourKey);
        }), map((tour: Mote) => {
            poolInfo.tour = tour;
            return poolInfo;
        }), catchError((err) => {
            if(err?.message && err.message.includes("Client is offline")) {
                return throwError(() => err);
            }
            // If tour access is denied
            return of(poolInfo);
        }));
    }

    /**
     * Pool vouchers and remove owner from all but one (for backwards compatibility)
     */
    public async poolVouchers(vouchers: Voucher[]): Promise<string> {
        // Make sure all vouchers are related to the same product
        const sameProduct = vouchers.every((voucher) => voucher.productKey == vouchers[0].productKey);
        if(!sameProduct) return;

        // Split vouchers in pooled and unpooled
        let pooledVouchers = vouchers.filter((voucher) => voucher.poolKey);
        let unpooledVouchers = vouchers.filter((voucher) => !voucher.poolKey);

        // Find existing poolKey or create a new one
        const poolKey = pooledVouchers.length > 0 ? pooledVouchers[0].poolKey : DatabaseModel.genKey();

        // Add poolKey to all unpooled vouchers
        const poolPromises = unpooledVouchers.map((voucher) => this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key) + "/poolKey").set(poolKey));

        // Make sure one voucher keeps its ownership, if there are no vouchers already pooled
        if(pooledVouchers.length <= 0) unpooledVouchers = unpooledVouchers.slice(1, unpooledVouchers.length);

        // Filter out all used vouchers
        unpooledVouchers = unpooledVouchers.filter((voucher) => !voucher.tourStartedOn);

        // Remove ownership from unpooled vouchers
        const claimedPromises = unpooledVouchers.map((voucher) => this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key) + "/claimedOn").set(null));
        const ownerPromises = unpooledVouchers.map((voucher) => this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key) + "/userKey").set(''));

        await Promise.all(poolPromises.concat(ownerPromises).concat(claimedPromises));
        return poolKey;
    }

    /**
     * Watch a list of ProductVoucherList given a list of voucher keys
     */
    public watchProductVoucherListsByKey(voucherKeys: string[]): Observable<ProductVoucherList[]> {
        return this.watchVoucherList(voucherKeys).pipe(switchMap((vouchers) => {
            let vouchersByProduct = {};
            // Add voucher to userVouchers map
            for(let voucher of vouchers) {
                if(!vouchersByProduct[voucher.productKey]) vouchersByProduct[voucher.productKey] = [];
                vouchersByProduct[voucher.productKey].push(voucher);
            }

            let productVoucherListsObs = Object.keys(vouchersByProduct).map((productKey) => {
                return this.watchProductVoucherList(productKey, vouchersByProduct[productKey]);
            });

            if(productVoucherListsObs.length <= 0) return of(null);
            return combineLatest(productVoucherListsObs);
        }));
    }

    /**
     * Watch a ProductVoucherList given a product key and a list of vouchers
     */
    private watchProductVoucherList(productKey: string, vouchers: Voucher[]): Observable<ProductVoucherList> {
        return this.productService.watchProduct(productKey).pipe(map((product) => {
            return { product: product, vouchers: vouchers };
        }));
    }

    /**
     * Watch list of vouchers of a specific product associated with a user
     */
    public watchUserProductVouchers(productKey: string, userKey: string): Observable<Voucher[]> {
        return this.watchUserVouchers(userKey).pipe(map((vouchers) => {
            return vouchers.filter(voucher => voucher.productKey == productKey);
        }));
    }

    /**
     * Verify whether a user has access to a given product
     * @param userKey Key for the user
     * @param productKey Key for the product
     * @returns `true`, if the user has access. Otherwise, `false`
     */
    public verifyUserProductAccess(userKey: string, productKey: string): Observable<boolean> {
        return this.watchUserProductVouchers(productKey, userKey).pipe(map(vouchers => {
            // Check whether a voucher which has not been redeemed exists
            let validVoucher = vouchers.find((voucher) => !voucher.redeemedOn);
            return validVoucher ? true : false;
        }));
    }

    /**
     * Verify whether a user has access to a given tour
     * @returns `true`, if the user has access. Otherwise, `false`
     */
    public async verifyUserTourAccess(userKey: string, tour: Mote, setTourStartedOn: boolean = true): Promise<boolean> {
        // If the tour is not product-locked (and user does not need a voucher), grand access
        if(!tour.productLocked) return true;
        // Otherwise, assume the user does not have tour access
        return this.userOwnsTour(userKey, tour, setTourStartedOn);
    }

    /**
     * Check if user owns a tour
     * @returns `true`, if the user owns the tour. Otherwise, `false`
     */
    public async userOwnsTour(userKey: string, tour: Mote, setTourStartedOn: boolean = true): Promise<boolean> {

        // Get all the user's vouchers
        const vouchers = await firstValueFrom(this.watchUserVouchers(userKey).pipe(catchError(() => of(null))));

        // And get keys for all products associated with the tour
        const productKeys = Object.keys(tour.products);

        // Find all user vouchers matching at least one product from the tour
        let matchingVouchers = vouchers.filter((voucher: Voucher) => productKeys.includes(voucher.productKey));
        const userOwnsTour = Boolean(matchingVouchers.length > 0);

        // If no matching vouchers have been started => set tourStartedOn for the first
        if(setTourStartedOn && userOwnsTour) {
            let tourNeverStarted = matchingVouchers.every((voucher) => !voucher.tourStartedOn);
            if(tourNeverStarted) this.setTourStartedOn(matchingVouchers[0]);
        }

        return userOwnsTour;
    }


    /**
     * Watch a specific voucher and the associated product (union)
     */
    public watchProductVoucher(productKey: string, voucherKey: string): Observable<ProductVoucher | null> {
        if(!productKey) return empty();
        // Create a product union without a voucher if no voucher key given
        if(!voucherKey) {
            return this.productService.watchProduct(productKey).pipe(map(product => {
                return {
                    product: product,
                    voucher: null
                }
            }));
        }
        // Watch product and voucher (reward)
        return combineLatest(this.productService.watchProduct(productKey), this.watchVoucher(voucherKey), (product, voucher) => {
            return {
                product: product,
                voucher: voucher
            }
        });
    }

    /**
     * Watch list of ProductVouchers associated with a user
     */
    public watchUserProductVoucherList(userKey: string): Observable<ProductVoucher[]> {
        return this.watchUserVouchers(userKey).pipe(switchMap((vouchers) => {
            let productVoucherObs = vouchers.map((voucher) => this.watchProductVoucher(voucher.productKey, voucher.key));
            return combineLatest(productVoucherObs);
        }));
    }

    /**
     * Watch updates of a specific voucher from the database
     */
    public watchVoucher(voucherKey: string): Observable<Voucher> {
        return this.af.database.object(MotesPaths.VOUCHER_DB_PATH(voucherKey)).snapshotChanges().pipe(map(snap => {
            if(!snap.key) return null;
            return new Voucher(snap.payload.val(), snap.key);
        }));
    }

    /**
     * Watch updates of a specific list of vouchers from the database
     */
    public watchVoucherList(voucherKeys: string[]): Observable<Voucher[]> {
        let observables = voucherKeys.map((key) => this.watchVoucher(key));
        // Merge all query observable outputs
        return combineLatest(...observables);
    }

    /**
     * Watch all vouchers (admin only)
     */
    public watchVouchers(fromDate: number, toDate: number = 0): Observable<Voucher[]> {
        let query: QueryFn;

        if(toDate > 0) {
            query = (query) => query.orderByChild('createdOn').startAt(fromDate).endAt(toDate);
        }
        else {
            query = (query) => query.orderByChild('createdOn').startAt(fromDate);
        }

        return this.af.database.list(MotesPaths.VOUCHER_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            return snapList.map(snap => new Voucher(snap.payload.val(), snap.key));
        }));
    }

    /**
     * Get list of vouchers associated with input user
     */
    public watchUserVouchers(userKey: string): Observable<Voucher[]> {
        if(!userKey) return of([]);
        // Get all vouchers belonging to this user
        let query = (ref) => ref.orderByChild('userKey').equalTo(userKey);

        return this.af.database.list(MotesPaths.VOUCHER_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            // Get payload json from firebase and add key in data (to be compatible with current impl)
            let vouchers = snapList.map((snap) => new Voucher(snap.payload.val(), snap.key));
            return vouchers;
        }));
    }

    /**
     * Get list of vouchers associated with input product
     */
    public watchProductVouchers(productKey: string): Observable<Voucher[]> {
        // Get all vouchers belonging to this product
        let query = (ref) => ref.orderByChild('productKey').equalTo(productKey);

        return this.af.database.list(MotesPaths.VOUCHER_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            // Get payload json from firebase and add key in data (to be compatible with current impl)
            let vouchers = snapList.map((snap) => new Voucher(snap.payload.val(), snap.key));
            return vouchers;
        }));
    }

    /**
     * Get payment with payment key
     */
    public getPayment(paymentKey: string): Promise<Payment> {
        // Get payment observer belonging to this payment key
        let paymentWatch = this.af.database.object(MotesPaths.PAYMENT_DB_PATH(paymentKey)).snapshotChanges().pipe(map(snapShot => {
            // Initiate payment instance
            return new Payment(snapShot.key, snapShot.payload.val());
        }));
        // Return promise of first payment from database
        return paymentWatch.pipe(first()).toPromise().catch(() => null);
    }

    /**
     * Get a specific voucher from the database one time
     */
    public getVoucher(voucherKey: string): Promise<Voucher> {
        return this.watchVoucher(voucherKey).pipe(first()).toPromise().catch(() => null);
    }

    /**
     * Claim a voucher (associate it with a specific user)
     */
    public async claimVoucher(voucher: Voucher, userKey: string): Promise<boolean> {
        if(!userKey) {
            console.warn("Could not claim voucher, no user given");
            return false;
        }

        // Claim voucher for the given user
        let result = await this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key)).update({ userKey: userKey, claimedOn: this.af.serverTime() }).catch(err => 'err');
        if(result == 'err') return false;

        // Create redeem stat event
        this.statsService.addStatsEventAddition(new StatEventVoucherClaimed({ voucherKey: voucher.key }));
        return true;
    }

    /**
     * Get pool key of vouchers from a claim code
     */
    public async getPoolKeyFromClaimCode(claimCode: string): Promise<string> {
        const query = (ref) => ref.orderByChild('claimCode').equalTo(claimCode);

        const voucherObs = this.af.database.list(MotesPaths.VOUCHER_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            if(!snapList) return [];
            return snapList.map((snap) => new Voucher(snap.payload.val(), snap.key));
        }));

        const voucherList = await firstValueFrom(voucherObs).catch( err => {
            console.log("Could not get voucher list")
            return [] as Voucher[];
        });
        // TODO: Add stat event to track this?
        // this.statsService.addStatsEventAddition(new StatEventVoucherClaimed({ voucherKey: voucher.key }));

        // Filter out child vouchers - it should only be possible to claim parent vouchers
        // (for now as some parent and child vouchers has got the same claim code and are created on the exact same time, so child might be first in list)
        const parentVouchers = voucherList.filter(voucher => !voucher.parent);
        // Return first parent voucher with claim code from the list
        return parentVouchers.length >= 1 ? parentVouchers[0].poolKey : "";
    }

    /**
     * Unclaim a voucher (testing purposes)
     */
    public async unclaimVoucher(voucherKey: string): Promise<boolean> {
        let result = await this.af.object(MotesPaths.VOUCHER_DB_PATH(voucherKey)).update({ userKey: '', claimedOn: null }).catch(err => 'err');
        if(result == 'err') return false;
        return true;
    }

    /**
     * Create and claim a reward/voucher for a user
     */
    public async claimReward(challenge: Challenge, userKey: string, email: string = '', quizPoints: number = 0, reward?: Product): Promise<Voucher | null> {
        if(!userKey) {
            console.error("Can not claim reward, no user key??");
            return null;
        }

        if(!challenge || challenge.rewardList.length <= 0) {
            console.error("Can not claim reward, no challenge??");
            return null;
        }

        // Use provided reward or use first reward in challenge list
        let rewardKey = reward ? reward.key : challenge.rewardList[0].key;

        // Test if the user already claimed the reward of this challenge
        let voucherList = await this.getUserProductVoucher(rewardKey, userKey);

        // Return existing voucher (which might already be redeemed)
        if(voucherList.length > 0) {
            let voucher = voucherList[0];
            // console.log("User already claimed reward of this product", voucher);
            return voucher;
        }

        // Create new voucher if challenge goal has been reached and not claimed yet
        let voucher = await this.createUserVoucher(rewardKey, userKey, email, quizPoints);

        // Automatically claim and redeem voucher
        if(reward && reward.type == ProductType.Digital) {
            await this.claimVoucher(voucher, userKey);
            await this.redeemVoucher(voucher);
        }

        // Check if voucher creation failed
        if(!voucher) {
            console.error("Could not create challenge reward voucher for challenge??", challenge);
            return null;
        }
        // Create redeem stat event
        this.statsService.addStatsEventAddition(new StatEventVoucherClaimed({ voucherKey: voucher.key }));

        return voucher;
    }

    /**
     * Save redeemed date in voucher 
     */
    public async redeemVoucher(voucher: Voucher): Promise<boolean> {
        await this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key) + "/redeemedOn").set(this.af.serverTime());
        // Create redeem stat event
        this.statsService.addStatsEventAddition(new StatEventVoucherRedeemed({ voucherKey: voucher.key }));
        return true;
    }

    /**
     * Save price in voucher 
     */
    public async saveVoucherPrice(voucher: Voucher): Promise<void> {
        return this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key) + "/price").set(voucher.price);
    }

    /**
     * Mark vouchers as manually migrated (sets manuallyMigratedOn trigger field)
     */
    public async markManuallyMigrated(voucherKeys: string[]): Promise<void> {
        const migratedOn = this.af.serverTime();
        await Promise.all(voucherKeys.map((key) =>
            this.af.object(MotesPaths.VOUCHER_DB_PATH(key) + "/manuallyMigratedOn").set(migratedOn)
        ));
    }

    /**
     * Mark voucher as refunded
     */
    public async refundVoucher(voucher: Voucher, reason: string): Promise<void> {
        return this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key)).update({
            refundedOn: Date.now(),
            refundedReason: reason || "",
            claimCode: ""
        });
    }

    /**
     * Make voucher available for give-away
     */
    public async giveAwayVoucher(voucher: Voucher): Promise<void> {
        // Set voucher parent
        this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key) + "/giveAway").set(true);

        // Set voucher children
        let voucherChildren = await this.getVoucherChildren(voucher.key);
        voucherChildren.forEach((voucherChild) => this.af.object(MotesPaths.VOUCHER_DB_PATH(voucherChild.key) + "/giveAway").set(true));
    }

    /**
     * Accept voucher as give-away: Give voucher a new user and reset give-away flag
     */
    public async acceptForeignVoucher(voucher: Voucher, newUserKey: string): Promise<boolean> {
        // Accept parent voucher
        this.acceptVoucher(voucher, newUserKey);
        // Accept voucher children
        let voucherChildren = await this.getVoucherChildren(voucher.key);
        voucherChildren.forEach((voucherChild) => this.acceptVoucher(voucherChild, newUserKey));

        return true;
    }

    private async acceptVoucher(voucher: Voucher, newUserKey: string): Promise<boolean> {
        await this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key) + "/userKey").set(newUserKey);
        await this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key) + "/giveAway").set(false);
        await this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key) + "/claimedOn").set(this.af.serverTime());
        return true;
    }

    public watchVoucherChildren(parentKey: string): Observable<Voucher[]> {
        let query = (ref) => ref.orderByChild('parent').equalTo(parentKey);

        return this.af.database.list(MotesPaths.VOUCHER_DB_PATH(), query).snapshotChanges().pipe(map(snapList => {
            if(!snapList) return [];
            let vouchers = snapList.map((snap) => new Voucher(snap.payload.val(), snap.key));
            return vouchers;
        }));
    }

    public getVoucherChildren(parentKey: string): Promise<Voucher[]> {
        return this.watchVoucherChildren(parentKey).pipe(take(1)).toPromise();
    }

    /**
     * Set tourStartedOn if it has not already been set
     */
    public async setTourStartedOn(voucher: Voucher): Promise<void> {
        if(voucher.tourStartedOn) return;
        return this.af.object(MotesPaths.VOUCHER_DB_PATH(voucher.key) + "/tourStartedOn").set(this.af.serverTime());
    }

    /**
     * Watch current user's add-ons for the active tour
     */
    public watchUserAddOns(): Observable<ProductVoucherList[]> {
        return this.watchCurrentUserVouchers().pipe(map(({ userVoucherLists, activeTourKey }) => {
            if(!userVoucherLists || !activeTourKey) return [];
            return userVoucherLists.filter((productVoucherList) => {
                // Find parent product (also owned by user)
                const parentKey = productVoucherList.product?.parent;
                const parent = userVoucherLists.find((pvl) => pvl.product?.key == parentKey)?.product;
                // Filter out all parent vouchers and vouchers not related to this tour
                return parent && (parent.tourKey == activeTourKey);
            });
        }));
    }

    /**
     * Watch user's add-ons belonging for the active tour, and group them with all add-ons in the same pool
     */
    public watchUserAddOnPools(): Observable<AddOnPool[]> {
        return this.watchCurrentUserVouchers().pipe(switchMap(({ userVoucherLists, activeTourKey }) => {
            if(!userVoucherLists || !activeTourKey) return [];
            // Collect poolKeys from user's parent vouchers
            let parentPoolKeys: { [poolKey: string]: boolean } = {};
            userVoucherLists.forEach((productVoucherList: ProductVoucherList) => {
                // Extract parent vouchers belonging to the active tour
                if(!productVoucherList.product.parent && (productVoucherList.product.tourKey == activeTourKey)) {
                    productVoucherList.vouchers.forEach((voucher) => parentPoolKeys[voucher.poolKey] = true);
                }
            });
            // Watch parent pools
            const parentPoolObs = Object.keys(parentPoolKeys).map((key) => this.watchPool(key));
            return combineLatest(parentPoolObs).pipe(map((parentLists: Voucher[][]) => {
                return { parentLists, userVoucherLists }
            }));
        }), switchMap(({ parentLists, userVoucherLists }: { parentLists: Voucher[][], userVoucherLists: ProductVoucherList[] }) => {
            const poolParents: Voucher[] = [].concat(...parentLists);
            const poolChildObs = poolParents.map((parent) => this.watchVoucherChildren(parent.key));
            // Watch child vouchers belonging to pool parents
            return combineLatest(poolChildObs).pipe(map((childLists: Voucher[][]) => {
                return { childLists, userVoucherLists };
            }));
        }), map(({ childLists, userVoucherLists }: { childLists: Voucher[][], userVoucherLists: ProductVoucherList[] }) => {
            const poolChildren: Voucher[] = [].concat(...childLists);
            return this.sortAddOns(poolChildren, userVoucherLists);
        }));
    }

    /**
     * Watch current user's vouchers and the active tour key
     */
    private watchCurrentUserVouchers(): Observable<{ userVoucherLists: ProductVoucherList[], activeTourKey: string }> {
        // Watch current user
        return this.loginService.watchUserRights().pipe(switchMap((userRights) => {
            if(!userRights || !userRights.uid || userRights.isAnonymous) return of({ projectEv: null, userRights: null });
            // Watch active tour
            return this.projectService.watchActiveProject().pipe(map((projectEv) => {
                return { projectEv, userRights };
            }));
        }), switchMap(({ projectEv, userRights }) => {
            if(!projectEv || !userRights || !projectEv.tour) return of({ userVoucherLists: [], activeTourKey: null });
            // Watch user vouchers
            return this.watchProductVoucherLists(userRights.uid).pipe(map((userVoucherLists) => {
                return { userVoucherLists, activeTourKey: projectEv.tour.key };
            }));
        }));
    }


    /**
     * Sort add-ons by their product
     */
    private sortAddOns(childVouchers: Voucher[], userVoucherLists: ProductVoucherList[]): AddOnPool[] {
        const addOnsMap: { [ productKey: string ]: AddOnPool } = {};

        for(const child of childVouchers) {
            // If product is not already in map, add it
            if(!addOnsMap[child.productKey]) {
                // Extract user's vouchers from this product
                const userVoucherList = userVoucherLists.find((pvl) => pvl.product?.key == child?.productKey);
                const childProduct = userVoucherList?.product;
                // Find user's first unredeemed voucher in list or just the first
                const ownVoucher = userVoucherList?.vouchers.find(voucher => !voucher.redeemedOn) || userVoucherList?.vouchers[0];
                const usedVoucherCount = ownVoucher.redeemedOn ? 1 : 0;
                // Add to map
                addOnsMap[child.productKey] = { product: childProduct, ownVoucher, otherVouchers: [], redeemedCount: usedVoucherCount };
            }
            // Avoid adding user's voucher to other vouchers
            if(addOnsMap[child.productKey].ownVoucher.key == child.key) continue;
            // Add other voucher to map
            addOnsMap[child.productKey].otherVouchers.push(child);
            addOnsMap[child.productKey].redeemedCount += child.redeemedOn ? 1 : 0;
        }

        return Object.values(addOnsMap);
    }
}

export interface AddOnPool {
    product: Product;
    ownVoucher: Voucher;
    otherVouchers: Voucher[];
    redeemedCount: number;
}

export interface ProductVoucherList {
    product: Product;
    vouchers: Voucher[];
}

export class PoolInfo extends DatabaseModel {
    public key: string;
    public availableVouchers: Voucher[] = [];
    public claimedVouchers: Voucher[] = [];
    public product: Product = null;
    public tour: Mote = null;
    public ownVoucher: Voucher = null;
    public claimCode: string = "";

    constructor(json?: any, key?: string) {
        super();
        if(json) this.fromJson(json);
        if(key) this.key = key;
    }

    /**
     * Reconstruct Voucher PoolInfo union from serialized data 
     */
    public fromJson(json: { key: string, availableVouchers: any, claimedVouchers: any, product: any, tour: any, ownVoucher: any }) {
        this.key = json.key;
        this.availableVouchers = this.fromJsonArray(json.availableVouchers, Voucher);
        this.claimedVouchers = this.fromJsonArray(json.claimedVouchers, Voucher);
        const allVouchers = this.availableVouchers.concat(this.claimedVouchers);
        this.claimCode = allVouchers?.length > 0 ? allVouchers[0].claimCode : "";
        this.product = json.product ? new Product(json.product.key, json.product) : null;
        this.tour = json.tour ? new Mote(json.tour.key, json.tour) : null;
        this.ownVoucher = json.ownVoucher ? new Voucher(json.ownVoucher, json.ownVoucher?.key) : null;
        return this;
    }

    /**
     * Serialize union of database models in Voucher PoolInfo 
     */
    public toJson(): ModelJson {
        return {
            key: this.key,
            availableVouchers: this.availableVouchers?.map( voucher => voucher.toJson()) || [],
            claimCode: this.claimCode,
            claimedVouchers: this.claimedVouchers?.map( voucher => voucher.toJson()) || [],
            product: this.product?.toJson() || null,
            tour: this.tour?.toJson() || null,
            ownVoucher: this.ownVoucher?.toJson() || null,
        }
    }
}
